import React from 'react';
import { useQuery } from 'react-query';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  EmojiEvents as AwardsIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as MatchedIcon,
} from '@mui/icons-material';
import { gebizAPI } from '../../services/api';

const SummaryCard = ({ title, value, subtitle, color, icon: Icon }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ color, fontWeight: 'bold', lineHeight: 1.2 }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        {Icon && (
          <Box 
            sx={{ 
              backgroundColor: `${color}15`, 
              borderRadius: 1, 
              p: 1, 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon sx={{ color, fontSize: 24 }} />
          </Box>
        )}
      </Box>
    </CardContent>
  </Card>
);

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === 0) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const GeBIZSummary = ({ jobId, onFilterChange }) => {
  const { data: summary, isLoading, error } = useQuery(
    ['gebizSummary', jobId],
    () => gebizAPI.getAwardsSummary(jobId),
    { enabled: !!jobId }
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load GeBIZ summary: {error.message}
      </Alert>
    );
  }

  if (!summary) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No GeBIZ awards data available for this job.
      </Alert>
    );
  }

  // Calculate totals
  const totalAwards = summary.total || 0;
  const totalValue = summary.total_value || 0;
  const matchedCount = (summary.exact || 0) + (summary.admin_confirmed || 0) + (summary.admin_corrected || 0);
  const newLeadCount = summary.new_lead || 0;
  const fuzzyReviewCount = summary.fuzzy_review || 0;

  const summaryCards = [
    {
      title: 'Total Awards',
      value: totalAwards,
      icon: AwardsIcon,
      color: '#1976d2',
    },
    {
      title: 'Total Value',
      value: formatCurrency(totalValue),
      icon: MoneyIcon,
      color: '#2e7d32',
    },
    {
      title: 'Matched',
      value: `${matchedCount}/${totalAwards}`,
      subtitle: newLeadCount > 0 ? `(${newLeadCount} new)` : undefined,
      icon: MatchedIcon,
      color: '#0288d1',
    },
  ];

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        GeBIZ Awards Summary
        {fuzzyReviewCount > 0 && (
          <Box
            component="span"
            sx={{
              backgroundColor: '#ed6c02',
              color: 'white',
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              fontSize: '0.75rem',
              fontWeight: 500,
              cursor: 'pointer',
            }}
            onClick={() => onFilterChange && onFilterChange('fuzzy_review')}
          >
            {fuzzyReviewCount} needs review
          </Box>
        )}
      </Typography>
      <Grid container spacing={2}>
        {summaryCards.map((card, index) => (
          <Grid item xs={12} sm={4} key={index}>
            <SummaryCard
              title={card.title}
              value={card.value}
              subtitle={card.subtitle}
              color={card.color}
              icon={card.icon}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default GeBIZSummary;
